/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackItemProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DonutChart } from 'src/core/components/DonutChart';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { LegendRow } from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/Legend/LegendRow';
import {
    Label,
    NumericLabel,
    StyledLink
} from 'src/luis/projects/analytics/components/IntentsWithErrorsCard/FaultyIntentDetails/Legend/LegendStyles';
import { LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import { Row } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';
import { FaultyIntent } from 'src/luis/projects/analytics/types/FaultyIntent';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type IncorrectProblemProps = {
    /**
     * The select faulty intent.
     */
    selectedIntent: IntentMetadata;

    /**
     * The conflicted intents with the selected intents.
     */
    incorrectIntents: FaultyIntent[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    incorrectProblemTitle: {
        id: 'analytics.faultyIntentDetails.incorrectProblemTitle',
        defaultMessage: 'Incorrect predictions'
    },
    incorrectProblemDescription: {
        id: 'analytics.faultyIntentDetails.incorrectProblemDescription',
        defaultMessage: 'Many utterances in this intent were incorrectly predicted.\n Consider ',
        description: 'to be continued in incorrectProblemDescriptionTwo'
    },
    incorrectProblemDescriptionTwo: {
        id: 'analytics.faultyIntentDetails.incorrectProblemDescriptionTwo',
        defaultMessage: 'adding more utterances to ',
        description: 'to be continued in incorrectProblemDescriptionThree'
    },
    incorrectProblemDescriptionThree: {
        id: 'analytics.faultyIntentDetails.incorrectProblemDescriptionThree',
        defaultMessage: ' or making these utterances more distinct:'
    },
    incorrectLabelPrefix: {
        id: 'analytics.faultyIntentDetails.incorrectLabelPrefix',
        defaultMessage: 'Predicted as'
    },
    otherLabel: {
        id: 'analytics.faultyIntentDetails.otherLabel',
        defaultMessage: 'Other intents'
    }
});

const { styleComponent } = getStylist('Dashboard');
const incorrectTheme = getScopedTheme('IncorrectProblem');

const Title = styleComponent<ITextProps>(Text)('Title', theme => ({
    fontSize: theme.xSmallFont,
    color: theme.defaultTextColor,
    lineHeight: theme.mediumLineHeight,
    letterSpacing: theme.xLargeLetterSpacing,
    fontWeight: theme.boldFont as Property.FontWeight
}));

const ChartContainer = styleComponent<IStackItemProps>(StackItem)('ChartContainer', theme => ({
    minWidth: theme.minSmallDonutWidth
}));

/**
 * Displays detailed information about the incorrect predictions problem
 * in the selected intent.
 */
export const IncorrectProblem = (props: IncorrectProblemProps) => {
    const { selectedIntent, incorrectIntents = [], onIntentClick } = props;
    const { localize } = useLocalization();

    return (
        <Stack horizontal tokens={{ childrenGap: 40 }} verticalAlign="center">
            <ChartContainer>
                <DonutChart size="small" width={32} {...getDonutChartConfig(selectedIntent, incorrectIntents)} />
            </ChartContainer>

            <Stack grow={1} tokens={{ childrenGap: 12 }}>
                <Title>{localize(messages.incorrectProblemTitle)}</Title>

                <Text style={{ whiteSpace: 'pre-line' }}>
                    {localize(messages.incorrectProblemDescription)}
                    <StyledLink
                        onClick={() =>
                            onIntentClick(selectedIntent.intentId, { differenceFilters: ['incorrect'], nearestRivalIntentsFilters: [] })
                        }>
                        {localize(messages.incorrectProblemDescriptionTwo)} {selectedIntent.intentName}
                    </StyledLink>
                    {localize(messages.incorrectProblemDescriptionThree)}
                </Text>

                <div>
                    {incorrectIntents.length > 0 && (
                        <LegendRow
                            intent={incorrectIntents[0]}
                            labelPrefix={localize(messages.incorrectLabelPrefix)}
                            legendColor="incorrect"
                            onIntentClick={() =>
                                onIntentClick(selectedIntent.intentId, {
                                    differenceFilters: ['incorrect'],
                                    nearestRivalIntentsFilters: [incorrectIntents[0].id]
                                })
                            }
                        />
                    )}
                    {incorrectIntents.length > 1 && (
                        <LegendRow
                            intent={incorrectIntents[1]}
                            labelPrefix={localize(messages.incorrectLabelPrefix)}
                            legendColor="lightIncorrect"
                            onIntentClick={() =>
                                onIntentClick(selectedIntent.intentId, {
                                    differenceFilters: ['incorrect'],
                                    nearestRivalIntentsFilters: [incorrectIntents[1].id]
                                })
                            }
                        />
                    )}
                    {incorrectIntents.length > 2 && (
                        <Row>
                            <Label>
                                <LegendItem color="other" />
                                <Text>{localize(messages.otherLabel)}</Text>
                            </Label>
                            <NumericLabel>
                                <Text>
                                    {selectedIntent.numIncorrectUtterances -
                                        incorrectIntents[0].utterancesCount -
                                        incorrectIntents[1].utterancesCount}
                                </Text>
                            </NumericLabel>
                        </Row>
                    )}
                </div>
            </Stack>
        </Stack>
    );
};

/**
 * Constructs the configuration of the donut chart like x axis data, y axis data and colors.
 *
 * @param intent The selected intent.
 * @param engagedIntents The conflicting intents with the selected intent.
 */
const getDonutChartConfig = (
    intent: IntentMetadata,
    engagedIntents: FaultyIntent[]
): { xData: ChartAxisData<string>; yData: ChartAxisData<number>; colors: string[] } => {
    const xData: ChartAxisData<string> = {
        label: 'Type',
        data: ['Correct', 'Unclear', 'TopConflictingIncorrectIntent', 'SecondIncorrectIntent', 'Other']
    };

    const otherUtteranceCount =
        intent.numIncorrectUtterances -
        (engagedIntents.length > 0 ? engagedIntents[0].utterancesCount : 0) -
        (engagedIntents.length > 1 ? engagedIntents[1].utterancesCount : 0);

    const yData: ChartAxisData<number> = {
        label: 'Ratio',
        data: [
            intent.numCorrectUtterances,
            intent.numUnclearUtterances,
            engagedIntents.length ? engagedIntents[0].utterancesCount : 0,
            engagedIntents.length > 1 ? engagedIntents[1].utterancesCount : 0,
            otherUtteranceCount
        ]
    };

    const colors = [
        incorrectTheme.fadedCorrectColor,
        incorrectTheme.fadedUnclearColor,
        incorrectTheme.incorrectColor,
        incorrectTheme.fadedIncorrectColor,
        incorrectTheme.othersColor
    ];

    return { xData, yData, colors };
};
